import { Link, useLocation } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { Card } from '../components/ui';
import { formatHours, planLabel } from '../utils/formatters';
import { linkButtonClass } from '../utils/linkButtonClass';
import Layout from '../components/Layout';

/**
 * プラン変更（アップグレード）の完了画面。
 *
 * アップグレードは Stripe のホストページを経由せずサーバ側で即時適用されるため、
 * 決済の完了画面（BillingSuccessPage）を通らない。結果を伝える場所がないと、
 * 日割りでいくら請求されたかを利用者が確認できないまま課金だけが起きる。
 *
 * 表示内容は遷移元（BillingPage）から navigate の state で受け取る。直リンクや
 * リロードで state を失った場合も、プラン変更自体は成立しているため一般的な完了文言に
 * 落として案内する（金額はお支払い履歴で確認できる）。
 */
export interface PlanChangedState {
  planType?: string;
  /** 実際に請求された日割り差額（円）。取得できなかった場合は null */
  chargedAmount?: number | null;
  hoursLimit?: number;
}

export default function PlanChangedPage() {
  const location = useLocation();
  const state = (location.state ?? {}) as PlanChangedState;
  const { planType, chargedAmount, hoursLimit } = state;

  return (
    <Layout>
      <div className="flex-1 flex items-center justify-center">
        <Card className="max-w-md w-full text-center">
          <div className="flex justify-center mb-6">
            <CheckCircle className="w-20 h-20 text-success" />
          </div>

          <h1 className="text-2xl font-bold mb-4">プランを変更しました</h1>

          {planType && (
            <div className="bg-info-bg border border-info/30 rounded-lg p-4 mb-6">
              <p className="text-info-fg font-bold text-lg">
                {planLabel(planType)}プランに変更されました
              </p>
              {hoursLimit !== undefined && (
                <p className="text-info-fg text-sm mt-1">
                  {hoursLimit === -1
                    ? '書き起こし時間は無制限になりました'
                    : `月の上限が${formatHours(hoursLimit, { unit: '時間', minutesOnlyBelowOneHour: true })}になりました`}
                </p>
              )}
            </div>
          )}

          <p className="text-gray-600 mb-2">
            新しい上限は<strong>今すぐ</strong>ご利用いただけます。
          </p>

          {chargedAmount != null && chargedAmount > 0 ? (
            <p className="text-gray-600 mb-6">
              残り期間の差額として<strong>¥{chargedAmount.toLocaleString()}</strong>を
              日割りでご請求しました（お支払い履歴に記載されます）。
            </p>
          ) : (
            <p className="text-gray-600 mb-6">
              残り期間の差額は日割りでご請求します。金額はお支払い履歴でご確認いただけます。
            </p>
          )}

          <div className="space-y-3">
            <Link to="/upload" className={linkButtonClass('primary', 'block w-full')}>
              音声をアップロード
            </Link>
            <Link to="/billing" className={linkButtonClass('secondary', 'block w-full')}>
              お支払い履歴を見る
            </Link>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
